import React, { useState, useEffect } from 'react';
import { ref, onValue, get } from 'firebase/database';
import { doc, setDoc, getDoc, onSnapshot } from 'firebase/firestore';
import { realtimeDb, db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { 
  Settings as SettingsIcon, 
  Database, 
  Server, 
  Activity, 
  AlertTriangle, 
  CheckCircle, 
  Clock,
  Users,
  Gamepad2,
  Coins,
  BarChart3,
  RefreshCw,
  Save,
  Trash2,
  Shield,
  Zap,
  CreditCard,
  DollarSign,
  Key,
  Eye,
  EyeOff
} from 'lucide-react';

interface SystemStatus {
  database: 'online' | 'offline' | 'error';
  games: number;
  users: number;
  activeGames: number;
  waitingGames: number;
  playingGames: number;
  completedGames: number;
  totalCoins: number;
  lastUpdate: number;
  errors: string[];
}

interface SystemConfig {
  maxWaitingGames: number;
  maxPlayingGames: number;
  autoCleanupInterval: number;
  gameTimeout: number;
  maintenanceMode: boolean;
  debugMode: boolean;
  stripe: {
    enabled: boolean;
    testMode: boolean;
    publishableKey: string;
    secretKey: string;
    webhookSecret: string;
    currency: string;
    minimumAmount: number;
    feePercentage: number;
  };
  createdBy?: string;
  createdAt?: string;
  updatedBy?: string;
  updatedAt?: string;
}

const Settings: React.FC = () => {
  const { currentUser } = useAuth();
  const [systemStatus, setSystemStatus] = useState<SystemStatus>({
    database: 'offline',
    games: 0,
    users: 0,
    activeGames: 0,
    waitingGames: 0,
    playingGames: 0,
    completedGames: 0,
    totalCoins: 0,
    lastUpdate: 0,
    errors: []
  });
  
  const [config, setConfig] = useState<SystemConfig>({
    maxWaitingGames: 100,
    maxPlayingGames: 200,
    autoCleanupInterval: 3600000, // 1 óra
    gameTimeout: 7200000, // 2 óra
    maintenanceMode: false,
    debugMode: false,
    stripe: {
      enabled: false,
      testMode: true,
      publishableKey: '',
      secretKey: '',
      webhookSecret: '',
      currency: 'USD',
      minimumAmount: 0.5,
      feePercentage: 2.9
    }
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loadSystemStatus = async () => {
      try {
        // Adatbázis kapcsolat tesztelése
        const testRef = ref(realtimeDb, '.info/connected');
        const unsubscribe = onValue(testRef, (snapshot) => {
          const connected = snapshot.val();
          setSystemStatus(prev => ({
            ...prev,
            database: connected ? 'online' : 'offline',
            lastUpdate: Date.now()
          }));
        });

        // Játékok statisztikái
        const gamesRef = ref(realtimeDb, 'games');
        const gamesSnapshot = await get(gamesRef);
        let waitingGames = 0;
        let playingGames = 0;
        let completedGames = 0;

        if (gamesSnapshot.exists()) {
          const games = gamesSnapshot.val();
          Object.values(games).forEach((game: any) => {
            if (game.status === 'waiting') waitingGames++;
            else if (game.status === 'playing') playingGames++;
            else if (game.status === 'completed') completedGames++;
          });
        }

        // Felhasználók száma
        const usersRef = ref(realtimeDb, 'users');
        const usersSnapshot = await get(usersRef);
        const users = usersSnapshot.exists() ? Object.keys(usersSnapshot.val()).length : 0;

        // Aranytallérok
        const coinsRef = ref(realtimeDb, 'coins');
        const coinsSnapshot = await get(coinsRef);
        let totalCoins = 0;
        if (coinsSnapshot.exists()) {
          const coins = coinsSnapshot.val();
          totalCoins = Object.values(coins).reduce((sum: number, userCoins: any) => 
            sum + (userCoins.balance || 0), 0);
        }

        setSystemStatus(prev => ({
          ...prev,
          games: waitingGames + playingGames + completedGames,
          users,
          activeGames: waitingGames + playingGames,
          waitingGames,
          playingGames,
          completedGames,
          totalCoins,
          lastUpdate: Date.now()
        }));

        setLoading(false);
        return () => unsubscribe();
      } catch (error) {
        console.error('Error loading system status:', error);
        
        // Mock adatok fejlesztéshez
        const mockSystemStatus: SystemStatus = {
          database: 'offline',
          games: 156,
          users: 42,
          activeGames: 8,
          waitingGames: 3,
          playingGames: 5,
          completedGames: 148,
          totalCoins: 12500,
          lastUpdate: Date.now(),
          errors: ['Firebase jogosultsági hiba - mock adatok használata']
        };
        
        setSystemStatus(mockSystemStatus);
        setLoading(false);
      }
    };

    loadSystemStatus();
    
    // Valós idejű konfiguráció betöltés Firestore-ból
    const configRef = doc(db, 'admin_settings', 'config');
    const unsubscribe = onSnapshot(
      configRef, 
      (snapshot) => {
        if (snapshot.exists()) {
          setConfig(snapshot.data() as SystemConfig);
        } else {
          // Alapértelmezett konfiguráció létrehozása, ha még nem létezik
          const defaultConfig = {
            ...config,
            createdBy: currentUser?.uid || 'system',
            createdAt: new Date().toISOString()
          };
          setDoc(configRef, defaultConfig).catch((error) => {
            console.error('Error creating default config:', error);
          });
        }
      }, 
      (error) => {
        console.error('Error loading configuration:', error);
        if (error.code !== 'permission-denied') {
          toast.error('Hiba történt a konfiguráció betöltésekor');
        }
      }
    );
    
    // Valós idejű frissítés - csak ha nincs hiba
    try {
      const interval = setInterval(loadSystemStatus, 30000); // 30 másodpercenként
      return () => {
        try {
          clearInterval(interval);
          unsubscribe(); // Cleanup Firestore listener
        } catch (cleanupError) {
          console.error('Error during Settings cleanup:', cleanupError);
        }
      };
    } catch (error) {
      console.error('Error setting up system status updates:', error);
      return () => {
        try {
          unsubscribe(); // Ensure Firestore listener is cleaned up even if interval fails
        } catch (cleanupError) {
          console.error('Error during Settings cleanup:', cleanupError);
        }
      };
    }
  }, []);





  const cleanupSystem = async () => {
    try {
      const now = Date.now();
      const gamesRef = ref(realtimeDb, 'games');
      const gamesSnapshot = await get(gamesRef);
      
      if (gamesSnapshot.exists()) {
        const games = gamesSnapshot.val();
        let deletedCount = 0;
        
        Object.entries(games).forEach(([id, game]: [string, any]) => {
          const gameAge = now - (game.createdAt || now);
          const shouldDelete = 
            (game.status === 'waiting' && gameAge > config.autoCleanupInterval) ||
            (game.status === 'playing' && gameAge > config.gameTimeout) ||
            (!game.createdAt && game.status === 'waiting');
          
          if (shouldDelete) {
            // Itt törölnénk a játékot
            deletedCount++;
          }
        });
        
        console.log(`Cleanup completed. ${deletedCount} old games would be deleted.`);
        alert(`Cleanup completed. ${deletedCount} old games would be deleted.`);
      }
    } catch (error) {
      console.error('Error during cleanup:', error);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    
    try {
      const configRef = doc(db, 'admin_settings', 'config');
      await setDoc(configRef, {
        ...config,
        updatedBy: currentUser?.uid || 'unknown',
        updatedAt: new Date().toISOString()
      });
      
      console.log('Settings saved successfully');
      toast.success('Beállítások sikeresen mentve!');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Hiba történt a beállítások mentése során!');
    } finally {
      setSaving(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online':
        return 'text-green-600 dark:text-green-400';
      case 'offline':
        return 'text-red-600 dark:text-red-400';
      case 'error':
        return 'text-yellow-600 dark:text-yellow-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'online':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'offline':
        return <AlertTriangle className="h-5 w-5 text-red-500" />;
      case 'error':
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      default:
        return <Activity className="h-5 w-5 text-gray-500" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex-auto">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Beállítások</h1>
          <p className="mt-2 text-sm sm:text-base text-gray-600 dark:text-gray-400">
            A Game Winners játék rendszer beállításai és konfigurációja
          </p>
        </div>
        <div className="flex justify-center">
          <button onClick={cleanupSystem} className="btn-secondary px-4 py-2">
            <RefreshCw className="mr-2 h-4 w-4" />
            Rendszer tisztítás
          </button>
        </div>
      </div>

      {/* System Status */}
      <div className="grid grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-2">
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Rendszer Állapot</h3>
          </div>
          <div className="card-body">
            <div className="space-y-3 sm:space-y-4">
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="flex items-center">
                  <Database className="h-5 w-5 text-blue-500 mr-3" />
                  <span className="text-sm sm:text-base font-medium text-gray-500 dark:text-gray-400">Adatbázis</span>
                </div>
                <div className="flex items-center space-x-2">
                  {getStatusIcon(systemStatus.database)}
                  <span className={`text-sm sm:text-base ${getStatusColor(systemStatus.database)}`}>
                    {systemStatus.database === 'online' ? 'Online' : 
                     systemStatus.database === 'offline' ? 'Offline' : 'Hiba'}
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="flex items-center">
                  <Server className="h-5 w-5 text-green-500 mr-3" />
                  <span className="text-sm sm:text-base font-medium text-gray-500 dark:text-gray-400">Játék szerver</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="text-sm sm:text-base text-green-600 dark:text-green-400">Aktív</span>
                </div>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="flex items-center">
                  <Shield className="h-5 w-5 text-purple-500 mr-3" />
                  <span className="text-sm sm:text-base font-medium text-gray-500 dark:text-gray-400">Biztonság</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="text-sm sm:text-base text-green-600 dark:text-green-400">Védett</span>
                </div>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="flex items-center">
                  <Zap className="h-5 w-5 text-yellow-500 mr-3" />
                  <span className="text-sm sm:text-base font-medium text-gray-500 dark:text-gray-400">Teljesítmény</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="text-sm sm:text-base text-green-600 dark:text-green-400">Jó</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Statisztikák</h3>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="flex items-center justify-center mb-2">
                  <Users className="h-5 w-5 text-blue-500" />
                </div>
                <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">{systemStatus.users}</p>
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Felhasználó</p>
              </div>
              
              <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="flex items-center justify-center mb-2">
                  <Gamepad2 className="h-5 w-5 text-green-500" />
                </div>
                <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">{systemStatus.activeGames}</p>
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Aktív játék</p>
              </div>
              
              <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="flex items-center justify-center mb-2">
                  <Coins className="h-5 w-5 text-yellow-500" />
                </div>
                <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">{systemStatus.totalCoins.toLocaleString()}</p>
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Aranytallér</p>
              </div>
              
              <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="flex items-center justify-center mb-2">
                  <BarChart3 className="h-5 w-5 text-purple-500" />
                </div>
                <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">{systemStatus.games}</p>
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Összes játék</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Configuration */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Rendszer Konfiguráció</h3>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="form-label sm:form-label-mobile">Max várakozó játékok</label>
              <input
                type="number"
                value={config.maxWaitingGames}
                onChange={(e) => setConfig({...config, maxWaitingGames: parseInt(e.target.value)})}
                className="form-input sm:form-input-mobile"
                placeholder="100"
              />
            </div>
            
            <div>
              <label className="form-label sm:form-label-mobile">Max aktív játékok</label>
              <input
                type="number"
                value={config.maxPlayingGames}
                onChange={(e) => setConfig({...config, maxPlayingGames: parseInt(e.target.value)})}
                className="form-input sm:form-input-mobile"
                placeholder="200"
              />
            </div>
            
            <div>
              <label className="form-label sm:form-label-mobile">Auto cleanup intervallum (ms)</label>
              <input
                type="number"
                value={config.autoCleanupInterval}
                onChange={(e) => setConfig({...config, autoCleanupInterval: parseInt(e.target.value)})}
                className="form-input sm:form-input-mobile"
                placeholder="3600000"
              />
            </div>
            
            <div>
              <label className="form-label sm:form-label-mobile">Játék timeout (ms)</label>
              <input
                type="number"
                value={config.gameTimeout}
                onChange={(e) => setConfig({...config, gameTimeout: parseInt(e.target.value)})}
                className="form-input sm:form-input-mobile"
                placeholder="7200000"
              />
            </div>
            
            <div className="flex items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <input
                type="checkbox"
                checked={config.maintenanceMode}
                onChange={(e) => setConfig({...config, maintenanceMode: e.target.checked})}
                className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label className="ml-3 text-sm sm:text-base text-gray-700 dark:text-gray-300">
                Karbantartási mód
              </label>
            </div>
            
            <div className="flex items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <input
                type="checkbox"
                checked={config.debugMode}
                onChange={(e) => setConfig({...config, debugMode: e.target.checked})}
                className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label className="ml-3 text-sm sm:text-base text-gray-700 dark:text-gray-300">
                Debug mód
              </label>
            </div>
          </div>
          

        </div>
      </div>

      {/* Stripe Configuration */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <CreditCard className="h-6 w-6 text-blue-500" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Stripe Konfiguráció</h3>
            </div>
            <div className="flex items-center space-x-2">
              {config.stripe.enabled ? (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Aktív
                </span>
              ) : (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                  Inaktív
                </span>
              )}
              {config.stripe.testMode && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                  Test mód
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 gap-6">
            {/* Enable/Disable Toggle */}
            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="flex items-center space-x-3">
                <CreditCard className="h-5 w-5 text-blue-500" />
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">Stripe Fizetések</h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Engedélyezi a Stripe fizetési szolgáltatást</p>
                </div>
              </div>
              <input
                type="checkbox"
                checked={config.stripe.enabled}
                onChange={(e) => setConfig({
                  ...config, 
                  stripe: { ...config.stripe, enabled: e.target.checked }
                })}
                className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
            </div>

            {/* Test Mode Toggle */}
            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="flex items-center space-x-3">
                <Shield className="h-5 w-5 text-yellow-500" />
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">Test Mód</h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Sandbox környezetben tesztelés</p>
                </div>
              </div>
              <input
                type="checkbox"
                checked={config.stripe.testMode}
                onChange={(e) => setConfig({
                  ...config, 
                  stripe: { ...config.stripe, testMode: e.target.checked }
                })}
                className="h-5 w-5 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
                disabled={!config.stripe.enabled}
              />
            </div>

            {/* API Keys */}
            {config.stripe.enabled && (
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="form-label">
                    <Key className="inline h-4 w-4 mr-2" />
                    Publishable Key {config.stripe.testMode ? '(Test)' : '(Live)'}
                  </label>
                  <input
                    type="text"
                    value={config.stripe.publishableKey}
                    onChange={(e) => setConfig({
                      ...config, 
                      stripe: { ...config.stripe, publishableKey: e.target.value }
                    })}
                    className="form-input"
                    placeholder={config.stripe.testMode ? 'pk_test_...' : 'pk_live_...'}
                  />
                </div>

                <div>
                  <label className="form-label">
                    <Key className="inline h-4 w-4 mr-2" />
                    Secret Key {config.stripe.testMode ? '(Test)' : '(Live)'}
                  </label>
                  <input
                    type="password"
                    value={config.stripe.secretKey}
                    onChange={(e) => setConfig({
                      ...config, 
                      stripe: { ...config.stripe, secretKey: e.target.value }
                    })}
                    className="form-input"
                    placeholder={config.stripe.testMode ? 'sk_test_...' : 'sk_live_...'}
                  />
                </div>

                <div>
                  <label className="form-label">
                    <Shield className="inline h-4 w-4 mr-2" />
                    Webhook Secret
                  </label>
                  <input
                    type="password"
                    value={config.stripe.webhookSecret}
                    onChange={(e) => setConfig({
                      ...config, 
                      stripe: { ...config.stripe, webhookSecret: e.target.value }
                    })}
                    className="form-input"
                    placeholder="whsec_..."
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="form-label">
                      <DollarSign className="inline h-4 w-4 mr-2" />
                      Pénznem
                    </label>
                    <select
                      value={config.stripe.currency}
                      onChange={(e) => setConfig({
                        ...config, 
                        stripe: { ...config.stripe, currency: e.target.value }
                      })}
                      className="form-input"
                    >
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="HUF">HUF</option>
                      <option value="GBP">GBP</option>
                    </select>
                  </div>

                  <div>
                    <label className="form-label">Minimum összeg</label>
                    <input
                      type="number"
                      step="0.1"
                      value={config.stripe.minimumAmount}
                      onChange={(e) => setConfig({
                        ...config, 
                        stripe: { ...config.stripe, minimumAmount: parseFloat(e.target.value) }
                      })}
                      className="form-input"
                      placeholder="0.50"
                    />
                  </div>

                  <div>
                    <label className="form-label">Díj (%)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={config.stripe.feePercentage}
                      onChange={(e) => setConfig({
                        ...config, 
                        stripe: { ...config.stripe, feePercentage: parseFloat(e.target.value) }
                      })}
                      className="form-input"
                      placeholder="2.9"
                    />
                  </div>
                </div>

                {/* Status Indicators */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                  <div className="flex items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div className="flex-shrink-0">
                      {config.stripe.publishableKey ? (
                        <CheckCircle className="h-5 w-5 text-green-500" />
                      ) : (
                        <AlertTriangle className="h-5 w-5 text-yellow-500" />
                      )}
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Publishable Key</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {config.stripe.publishableKey ? 'Beállítva' : 'Hiányzik'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div className="flex-shrink-0">
                      {config.stripe.secretKey ? (
                        <CheckCircle className="h-5 w-5 text-green-500" />
                      ) : (
                        <AlertTriangle className="h-5 w-5 text-yellow-500" />
                      )}
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Secret Key</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {config.stripe.secretKey ? 'Beállítva' : 'Hiányzik'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div className="flex-shrink-0">
                      {config.stripe.webhookSecret ? (
                        <CheckCircle className="h-5 w-5 text-green-500" />
                      ) : (
                        <AlertTriangle className="h-5 w-5 text-yellow-500" />
                      )}
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Webhook</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {config.stripe.webhookSecret ? 'Beállítva' : 'Hiányzik'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Test Connection Button */}
                <div className="flex justify-center pt-4">
                  <button
                    onClick={() => {
                      // Itt lehet majd Stripe kapcsolat tesztelése
                      console.log('Testing Stripe connection...');
                    }}
                    disabled={!config.stripe.publishableKey || !config.stripe.secretKey}
                    className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Zap className="mr-2 h-4 w-4" />
                    Stripe Kapcsolat Tesztelése
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Save All Settings */}
      <div className="flex flex-col items-center py-6 space-y-4">
        <button
          onClick={saveSettings}
          disabled={saving}
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed px-8 py-3 text-lg"
        >
          <Save className="mr-3 h-5 w-5" />
          {saving ? 'Mentés...' : 'Összes Beállítás Mentése'}
        </button>
        
        {/* Configuration Metadata */}
        {(config.updatedAt || config.createdAt) && (
          <div className="text-xs text-gray-500 dark:text-gray-400 text-center">
            {config.updatedAt && (
              <p>Utoljára módosítva: {new Date(config.updatedAt).toLocaleString('hu-HU')}</p>
            )}
            {config.createdAt && (
              <p>Létrehozva: {new Date(config.createdAt).toLocaleString('hu-HU')}</p>
            )}
          </div>
        )}
      </div>

      {/* Error Log */}
      {systemStatus.errors.length > 0 && (
        <div className="card border-red-200 dark:border-red-700">
          <div className="card-header">
            <h3 className="text-lg font-semibold text-red-900 dark:text-red-100">Hibák</h3>
          </div>
          <div className="card-body">
            <div className="space-y-2">
              {systemStatus.errors.map((error, index) => (
                <div key={index} className="flex items-center">
                  <AlertTriangle className="h-4 w-4 text-red-500 mr-2" />
                  <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings; 